import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";
import clsx from "clsx";
import {
  CanMakePaymentResult,
  PaymentIntent,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  SetupIntent,
  StripeCardNumberElement,
} from "@stripe/stripe-js";
import {
  PaymentRequestCompleteStatus,
} from "@stripe/stripe-js/types/stripe-js/payment-request";
import dayjs from "dayjs";
import { AxiosError } from "axios";

import Button from "apps/website/components/base/Button/Button";
import Icon from "apps/website/components/base/Icon/Icon";
import Column from "apps/website/components/layout/Column/Column";
import Grid from "apps/website/components/layout/Grid/Grid";
import Form from "apps/website/components/form/Form/Form";
import { inputStyles } from "apps/website/components/form/Input/Input";
import Label from "apps/website/components/form/Label/Label";
import FormError from "apps/website/components/form-service/fields/Error";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { useAPI } from "apps/website/hooks/useAPI";
import {
  getActionTrackingInfo,
} from "apps/website/utils/tracking/getActionTrackingInfo";
import {
  getFlowFieldValuesForAction,
  useCatsStore,
  useCustomerStore,
  useDiscountStore,
  useNewFormsServiceStore,
} from "@./state";
import ProcessingPaymentModal
  from "apps/website/components/feature/Modal/ProcessingPaymentModal/ProcessingPaymentModal";
import { useFlow } from "apps/website/contexts/flow";
import Section from "apps/website/components/layout/Section/Section";
import Container from "apps/website/components/layout/Container/Container";
import { usePurchaseStore } from "libs/state/src/lib/stores/usePurchaseStore";
import { useDatadog } from "@auth/client-sdk-react";
import { PRODUCT_CATEGORIES } from "libs/form-utils/src/lib/product-categories";
import {
  ICheckoutV2SetupIntentData,
  ICreateAccountActionResponseFailedOn,
} from "@forms/schema";
import PayPalButton from "apps/website/components/feature/PayPalButton/PayPalButton";
import { scrollToFirstError } from "apps/website/utils/dom/scrollToError";
import {
  formatIntentType,
  getIntentClientSecretType,
  IntentClientSecretType,
} from "@api-clients/checkout";
import { isTransientError } from "apps/website/utils/api/isTransientError";

import {
  IIntentConfirmationResultError,
  IIntentConfirmationResultFailure,
  TIntentConfirmationResult,
} from "../../../../types/payment";

import { processingModalPhraseMap } from "./CardDetailsFormCheckoutV2.map";
import { DisplayState } from "@/constants/state";
import { DeliveryDateProductType, CheckoutStatus } from "@/primary-models";

const cardInputClasses = `${inputStyles} flex items-center cursor-text [&>*]:w-full [&>*]:font-display`;
const cardInputOptions = { style: { base: { fontFamily: "GreedBold, sans-serif", fontWeight: "900", fontSize: "20px" } } };

const DEFAULT_QUICK_CHECKOUT_ERROR = "Something went wrong, please checkout manually below.";
const DEFAULT_CHECKOUT_ERROR = "Something went wrong, please try another card or check your details and try again.";

export type PaymentDetailsType = "default" | "quickCheckout";
export interface ICardDetailsFormCheckoutV2 {
  isDisabled?: boolean;
  flowSlug: string;
  formUsed: string,
  onSuccess(): void;
  paymentType?: PaymentDetailsType;
  price?: number;
}

const CardDetailsFormCheckoutV2: FC<ICardDetailsFormCheckoutV2> = ({
  isDisabled = false,
  flowSlug,
  formUsed,
  onSuccess,
  paymentType = "default",
  price,
}) => {
  const {
    FormActions: formsActionsAPI,
    Checkout: checkout,
    DeliveryDates,
  } = useAPI();

  const posthog = usePostHog();
  const datadog = useDatadog();
  const [ cookies ] = useCookies([ "_fbp", "_fbc" ]);
  const { setCanUseQuickCheckout, setPurchaseInProgress } = usePurchaseStore();
  const { setFlowFieldValue, getActiveFieldValue, setFlowFieldError } = useNewFormsServiceStore();

  const stripe = useStripe();
  const elements = useElements();
  const [ error, setError ] = useState<string | undefined>(undefined);
  const [ isCardNumberComplete, setIsCardNumberComplete ] = useState<boolean>(false);
  const [ isCVCComplete, setIsCVCComplete ] = useState<boolean>(false);
  const [ isExpiryComplete, setIsExpiryComplete ] = useState<boolean>(false);
  const [ isCardDetailsComplete, setIsCardDetailsComplete ] = useState<boolean>(false);
  const [ paymentState, setPaymentState ] = useState<DisplayState>(DisplayState.READY);
  const [ paymentRequest, setPaymentRequest ] = useState<PaymentRequest | null>(null);
  const [ paymentRequestResult, setPaymentRequestResult ] = useState<CanMakePaymentResult | null>(null);
  const [ quickCheckoutError, setQuickCheckoutError ] = useState<string | undefined>(undefined);
  const [ showPayPalButton, setShowPayPalButton ] = useState<boolean>(false);

  const customerStore = useCustomerStore();
  const { cats } = useCatsStore();
  const discountStore = useDiscountStore();

  const { flow } = useFlow();
  /** This will use formsActions to try to save a basket and start the checkout.
     * Several outcomes are possible:
     * 1. If all goes well, it will store the checkout data in a basket,
     * create a stripeIntent and return the SetupIntent or PaymentIntent's clientSideSecret and the basket's ID
     * 2. If a checkout is already in progress for this user and has not timed out,
     * the existing basketId will be returned so that the checkout can be "resumed"
     * 3. If there is a problem creating the basket or the stripeIntent, an error message is returned
     */
  const getStripeIntent = useCallback(async (): Promise<({
    success: true,
  } & ICheckoutV2SetupIntentData) | {
    success: false,
    message: string,
    failedOn?: ICreateAccountActionResponseFailedOn,
  }> => {
    const actionTrackingInfo = getActionTrackingInfo(cookies);
    const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowSlug);

    const discountCode = discountStore.discountCodes.find((discount) => discount.flowId === flowSlug)?.discountCode;
    const customerId = customerStore.customerIds.find((customer) => customer.flowId === flowSlug)?.customerId;

    datadog.logger.info(`[CardDetailsFormCheckoutV2] getSetupIntent - START - flowSlug: ${flowSlug}, discountCode: ${discountCode}, customerId: ${customerId}`);

    if (customerId) {
      fieldSubmitValuesMap.set("customerId", {
        value: customerId,
        linkingId: "0",
      });
    } else {
      const message = `No customerId for flow ${flowSlug}`;
      datadog.logger.error(message, {
        flowSlug,
        discountCodes: [ ...discountStore.discountCodes ],
        customerIds: [ ...customerStore.customerIds ],
      });
      return {
        success: false,
        message,
      };
    }

    if (discountCode) {
      datadog.logger.info(`[CardDetailsFormCheckoutV2] Adding discount code to fieldSubmitValuesMap - flowSlug: ${flowSlug}, discountCode: ${discountCode}`);
      fieldSubmitValuesMap.set("discountCode", {
        value: discountCode,
        linkingId: "0",
      });
    } else {
      const message = `No discount for flow ${flowSlug}`;
      datadog.logger.warn(`[CardDetailsFormCheckoutV2] No discount code found for flow - flowSlug: ${flowSlug}`);
      datadog.logger.warn(message, {
        flowSlug,
        discountCodes: [ ...discountStore.discountCodes ],
        customerIds: [ ...customerStore.customerIds ],
      });
    }

    const catsArr = cats.find((dc) => dc.flowId === flowSlug)?.catIds || [];
    fieldSubmitValuesMap.set("catIds", { value: catsArr });

    const actionName = flow.mappedFlow.product === "FRESH_TRIAL"
      ? "checkoutFreshNewCustomerV2"
      : "checkoutLitterNewCustomerV2";

    datadog.logger.info(`[CardDetailsFormCheckoutV2] Performing action - actionName: ${actionName}, flowSlug: ${flowSlug}, product: ${flow.mappedFlow.product}, discountCode: ${discountCode}`, {
      actionName,
      flowSlug,
      product: flow.mappedFlow.product,
      discountCode,
    });

    const stripeIntentResult = await formsActionsAPI.performAction<ICheckoutV2SetupIntentData>(
      actionName,
      fieldSubmitValuesMap,
      flow.mappedFlow.product!,
      formUsed,
      undefined,
      posthog.featureFlags.getFlagVariants(),
      actionTrackingInfo,
      datadog.logger,
    );

    if (!stripeIntentResult.success) {
      const result = {
        ...stripeIntentResult,
        success: false,
        message: stripeIntentResult.message ?? "There was a problem preparing your card data",
      } as const;
      datadog.logger.error(`SetupIntentResultFailure: ${result.message}`, {
        eventName: "SetupIntentResultFailure",
        result,
        "data-component": CardDetailsFormCheckoutV2.name,
        discountCode,
      });
      return result;
    }

    datadog.logger.info("SetupIntentResultSuccess", {
      eventName: "SetupIntentResultSuccess",
      result: stripeIntentResult,
      "data-component": CardDetailsFormCheckoutV2.name,
      discountCode,
    });

    return {
      ...stripeIntentResult.responseBody,
      success: true,
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    posthog,
    datadog,
    cookies,
    customerStore.customerIds,
    discountStore.discountCodes,
    flow.mappedFlow.product,
    flowSlug,
    formUsed,
    formsActionsAPI,
    getActiveFieldValue,
    setFlowFieldError,
  ]);

  /** @param paymentData If truthy, then the quick-checkout error is set.
     * If PaymentRequestPaymentMethodEvent, then it will also be completed
     * with the given paymentDataFailureReason failure reason or "fail" if
     * no reason is provided.
     * @param errorString The error that will be displayed.
     * @param paymentDataFailureReason The error to fail quick-checkout with.
     */
  const handleError: (
    paymentData?: PaymentRequestPaymentMethodEvent | boolean,
    errorString?: string,
    paymentDataFailureReason?: Exclude<PaymentRequestCompleteStatus, "success">,
    failedOn?: ICreateAccountActionResponseFailedOn | undefined,
  ) => void = (paymentData, errorString, paymentDataFailureReason, failedOn) => {
    datadog.logger.error(`Setting error state: ${errorString}`, {
      eventName: "HandleError",
      paymentData,
      errorString,
      paymentDataFailureReason,
      "data-component": CardDetailsFormCheckoutV2.name,
    });
    if (paymentData) {
      if (typeof paymentData !== "boolean") {
        paymentData.complete(paymentDataFailureReason ?? "fail");
      }
      setQuickCheckoutError(errorString ?? DEFAULT_QUICK_CHECKOUT_ERROR);
      setError(undefined);
      setPaymentState(DisplayState.READY);
    } else {
      setQuickCheckoutError(undefined);
      if (failedOn?.fieldName === "existingUser") {
        setFlowFieldError(flowSlug, "existingUser", {
          name: "existingUser",
          message: "User already exists",
        });
      } else {
        setError(errorString ?? DEFAULT_CHECKOUT_ERROR);
      }

      setPaymentState(DisplayState.ERROR);
    }
    setPurchaseInProgress(false);
    scrollToFirstError();
  };

  const confirmSetupIntentQuickCheckout = async (
    { setupIntentClientSecret, paymentData }:
    { setupIntentClientSecret: string, paymentData: PaymentRequestPaymentMethodEvent },
  ): Promise<TIntentConfirmationResult> => {
    let intent = await stripe?.confirmCardSetup(
      setupIntentClientSecret,
      { payment_method: paymentData.paymentMethod.id },
      { handleActions: false },
    );
    // If we need to confirm the card, try to do that
    if (intent?.setupIntent?.status === "requires_action") {
      datadog.logger.info(`confirmPaymentData quickCheckout attempting to re-confirm card: ${intent.setupIntent.status}`, {
        eventName: "ConfirmPaymentDataQuickCheckoutReconfirm",
        setupIntentClientSecret,
        paymentData,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      intent = await stripe?.confirmCardSetup(setupIntentClientSecret);
    }
    // Check if the setupIntent was completed without error
    if (!intent || intent?.error || !intent?.setupIntent) {
      datadog.logger.error("confirmPaymentData quickCheckout failed", {
        eventName: "ConfirmPaymentDataQuickCheckoutFailed",
        setupIntentClientSecret,
        paymentData,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData, intent?.error?.message);
      return { success: false, intent: intent?.setupIntent, intentFailed: true };
    }
    // Else, the setupIntent completed without error
    const { setupIntent } = intent;
    // If the customer has not set up their card properly, we still consider that an error
    if (setupIntent.status !== "succeeded") {
      datadog.logger.error(`confirmPaymentData quickCheckout did not produce successful setupIntent: ${setupIntent.status}`, {
        eventName: "ConfirmPaymentDataQuickCheckoutBadStatus",
        setupIntentClientSecret,
        paymentData,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData, `Card setup failed: ${setupIntent.status}`);
      return { success: false, intent: setupIntent, intentFailed: true };
    }
    // Else, all went well
    paymentData.complete("success");
    return { success: true, intent: setupIntent, intentFailed: false };
  };

  const confirmPaymentIntentQuickCheckout = async (
    { paymentIntentClientSecret, paymentData }:
    { paymentIntentClientSecret: string, paymentData: PaymentRequestPaymentMethodEvent },
  ): Promise<TIntentConfirmationResult> => {
    let intent = await stripe?.confirmCardPayment(
      paymentIntentClientSecret,
      { payment_method: paymentData.paymentMethod.id },
      { handleActions: false },
    );
    // If we need to confirm the card, try to do that
    if (intent?.paymentIntent?.status === "requires_action") {
      datadog.logger.info(`confirmPaymentData quickCheckout attempting to re-confirm card: ${intent.paymentIntent.status}`, {
        eventName: "ConfirmPaymentDataQuickCheckoutReconfirm",
        paymentIntentClientSecret,
        paymentData,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      intent = await stripe?.confirmCardPayment(paymentIntentClientSecret);
    }
    // Check if the paymentIntent was completed without error
    if (!intent || intent?.error || !intent?.paymentIntent) {
      datadog.logger.error("confirmPaymentData quickCheckout failed", {
        eventName: "ConfirmPaymentDataQuickCheckoutFailed",
        paymentIntentClientSecret,
        paymentData,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData, intent?.error?.message);
      return { success: false, intent: intent?.paymentIntent, intentFailed: true };
    }
    // Else, the paymentIntent completed without error
    const { paymentIntent } = intent;
    // If the payment was not completed successfully, we still consider that an error
    if (paymentIntent.status !== "succeeded") {
      datadog.logger.error(`confirmPaymentData quickCheckout did not produce successful paymentIntent: ${paymentIntent.status}`, {
        eventName: "ConfirmPaymentDataQuickCheckoutBadStatus",
        paymentIntentClientSecret,
        paymentData,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData, `Card setup failed: ${paymentIntent.status}`);
      return { success: false, intent: paymentIntent, intentFailed: true };
    }
    // Else, all went well
    paymentData.complete("success");
    return { success: true, intent: paymentIntent, intentFailed: false };
  };

  const confirmSetupIntentDefaultCheckout = async ({
    setupIntentClientSecret,
    cardElement,
  }: {
    setupIntentClientSecret: string,
    cardElement: StripeCardNumberElement,
  }): Promise<TIntentConfirmationResult> => {
    const intent = await stripe?.confirmCardSetup(setupIntentClientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    // Check for errors
    if (!intent || intent?.error || !intent?.setupIntent) {
      datadog.logger.error("Setup intent failed", {
        eventName: "ConfirmPaymentDataCardFailed",
        setupIntentClientSecret,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(undefined, intent?.error?.message);
      return { success: false, intent: intent?.setupIntent, intentFailed: true };
    }

    // Else, all went well
    return { success: true, intent: intent.setupIntent, intentFailed: false };
  };

  const confirmPaymentIntentDefaultCheckout = async ({
    paymentIntentClientSecret,
    cardElement,
  }: {
    paymentIntentClientSecret: string,
    cardElement: StripeCardNumberElement,
  }): Promise<TIntentConfirmationResult> => {
    const intent = await stripe?.confirmCardPayment(paymentIntentClientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    // Check for errors
    if (!intent || intent?.error || !intent?.paymentIntent) {
      datadog.logger.error("Payment intent failed", {
        eventName: "ConfirmPaymentDataCardFailed",
        paymentIntentClientSecret,
        intent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(undefined, intent?.error?.message);
      return { success: false, intent: intent?.paymentIntent, intentFailed: true };
    }

    // Else, all went well
    return { success: true, intent: intent.paymentIntent, intentFailed: false };
  };

  const logInvalidPaymentIntentType = ({
    intentType,
    intentClientSecret,
    paymentData,
  }: {
    intentType: IntentClientSecretType | null,
    intentClientSecret: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
  }): void => {
    datadog.logger.error("Invalid intent type", {
      eventName: "ConfirmPaymentDataInvalidIntentType",
      intentType,
      intentClientSecret,
      paymentData,
      "data-component": CardDetailsFormCheckoutV2.name,
    });
  };

  /** Will try to confirm a SetupIntent or PaymentIntent, possibly starting a two-factor authentication flow
     * @param intentClientSecret The client secret of the intent we're currently trying to confirm
     * @param paymentData If provided, the data used for quick-checkout.
     * This function will set the result of the event, one way or another,
     * so after it has been called, you no longer need to worry about setting its status.
     * @return the confirmed SetupIntent or PaymentIntent, or undefined if the confirmation failed
     */
  const confirmPaymentData: (
    intentClientSecret: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
  ) => Promise<TIntentConfirmationResult> = async (
    intentClientSecret,
    paymentData,
  ) => {
    const intentType = getIntentClientSecretType(intentClientSecret);
    if (!stripe) {
      datadog.logger.error("confirmPaymentData called with no stripe", {
        eventName: "ConfirmPaymentDataNoStripe",
        intentClientSecret,
        paymentData,
        intentType,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData);
      return { success: false, intentFailed: false };
    }
    // If this is a quick checkout
    if (paymentData) {
      if (intentType === IntentClientSecretType.SETUP) {
        const setupIntent = await confirmSetupIntentQuickCheckout({
          setupIntentClientSecret: intentClientSecret, paymentData });
        return setupIntent;
      }
      if (intentType === IntentClientSecretType.PAYMENT) {
        const paymentIntent = await confirmPaymentIntentQuickCheckout({
          paymentIntentClientSecret: intentClientSecret, paymentData });
        return paymentIntent;
      }

      logInvalidPaymentIntentType({ intentType, intentClientSecret, paymentData });
      handleError(paymentData);
      return { success: false, intentFailed: false };
    }
    // Else, this is not a quick checkout
    const cardElement = elements?.getElement("cardNumber");
    // Check that the card exists
    if (!cardElement) {
      datadog.logger.error("Missing card element on payment details submission.", {
        eventName: "ConfirmPaymentDataCardNoCardElement",
        intentClientSecret,
        cardElement,
        intentType,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError();
      return { success: false, intentFailed: false };
    }

    if (intentType === IntentClientSecretType.SETUP) {
      const setupIntent = await confirmSetupIntentDefaultCheckout({
        setupIntentClientSecret: intentClientSecret, cardElement });
      return setupIntent;
    }
    if (intentType === IntentClientSecretType.PAYMENT) {
      const paymentIntent = await confirmPaymentIntentDefaultCheckout({
        paymentIntentClientSecret: intentClientSecret, cardElement });
      return paymentIntent;
    }

    logInvalidPaymentIntentType({ intentType, intentClientSecret, paymentData });
    handleError();
    return { success: false, intentFailed: false };
  };

  const validateDeliveryDate = async (): Promise<boolean> => {

    // Validate delivery date before proceeding
    const deliveryDateField = "freshDeliveryDate";
    const deliveryDateValue = getActiveFieldValue(flowSlug, deliveryDateField, "0");

    if (!deliveryDateValue?.data?.submitValue) {
      return true;
    }
    // Check if the delivery date is selectable

    const selectedDate = dayjs(deliveryDateValue.data.submitValue as string);

    // Map KatKinProduct to DeliveryDateProductType
    const mapProductType = (product: string | undefined): DeliveryDateProductType | undefined => {
      if (!product) { return undefined; }
      if (product === "HEALTH_SCOOP_LITTER") { return "HEALTH_SCOOP"; }
      if (product === "PLANET_SCOOP_LITTER") { return "PLANET_SCOOP"; }
      if (product === "FRESH" || product === "FRESH_TRIAL" || product === "FRESH_CROSS_SELL" || product === "FRESH_SAVER" || product === "FRESH_FREEZER") { return "FRESH"; }
      return undefined;
    };

    try {
      const result = await DeliveryDates.checkDeliveryDateSelectable(
        selectedDate,
        mapProductType(flow.mappedFlow.product),
        "STARTER",
      );

      if (!result.isSelectable) {
        const message = `We're sorry, the delivery date ${selectedDate.format("YYYY-MM-DD")} is no longer available, please choose another.`;
        setFlowFieldError(flowSlug, deliveryDateField, { message }, "0");
        setPaymentState(DisplayState.READY);
        scrollToFirstError();
        return false;
      }
      return true;
    } catch (validationError) {
      datadog.logger.error("Failed to validate delivery date", {
        eventName: "ValidateDeliveryDateError",
        deliveryDate: selectedDate.format("YYYY-MM-DD"),
        flowSlug,
        error: validationError,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      setFlowFieldError(flowSlug, deliveryDateField, { message: "Failed to validate delivery date. Please try again." }, "0");
      setPaymentState(DisplayState.READY);
      return false;
    }

  };

  const handlePollingExistingBasket = async (
    basketId: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
    setup?: ICheckoutV2SetupIntentData & { success: true },
  ): Promise<void> => {
    datadog.logger.debug(`Action returned existing basket ID '${basketId}', polling basket`, {
      eventName: "PollingExistingBasket",
      setup,
      "data-component": CardDetailsFormCheckoutV2.name,
    });

    const result = await checkout.pollBasket(basketId);

    if (result === null) {
      datadog.logger.error(`Timed out while waiting for existing basket '${basketId}' to complete`, {
        eventName: "PollingExistingBasketTimedOut",
        setup,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData);
      return;
    }

    if (result.status === CheckoutStatus.SUCCESS) {
      datadog.logger.info(`Existing basket '${basketId}' complete with status '${result.status}'`, {
        eventName: "PollingExistingBasketSuccessful",
        setup,
        result,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      setPaymentState(DisplayState.COMPLETE);
      onSuccess();
    } else {
      datadog.logger.error(`Existing basket '${basketId}' complete with status '${result.status}'`, {
        eventName: "PollingExistingBasketUnsuccessful",
        setup,
        result,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(paymentData);
    }
  };

  const handleExistingBasket = async (
    basketId: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
    setup?: ICheckoutV2SetupIntentData & { success: true },
  ): Promise<void> => {
    datadog.logger.debug(`Action returned existing basket ID '${basketId}', confirming basket`, {
      eventName: "ActionReturnedExistingBasket",
      setup,
      "data-component": CardDetailsFormCheckoutV2.name,
    });

    try {
      const result = await checkout.postPaymentsBasketConfirmed({ basketId });
      if (result.status === CheckoutStatus.SUCCESS) {
        datadog.logger.info(`Existing basket '${basketId}' confirmation successful with status '${result.status}'`, {
          eventName: "ExistingBasketSuccessful",
          setup,
          result,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        setPaymentState(DisplayState.COMPLETE);
        onSuccess();
      } else {
        datadog.logger.error(`Existing basket '${basketId}' confirmation failed with status '${result.status}'`, {
          eventName: "ExistingBasketUnsuccessful",
          setup,
          result,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        handleError(paymentData);
      }
    } catch (basketConfirmationError) {
      if (isTransientError(basketConfirmationError)) {
        await handlePollingExistingBasket(basketId, paymentData, setup);
      } else {
        const errorStatusMessage = basketConfirmationError instanceof AxiosError ? `with status '${basketConfirmationError.status}'` : "";
        datadog.logger.error(`Existing basket '${basketId}' confirmation failed ${errorStatusMessage}`, {
          eventName: "ExistingBasketUnsuccessful",
          setup,
          basketConfirmationError,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        handleError(paymentData);
      }
    }
  };

  const handlePollingNewBasket = async (
    basketId: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
    intentData?: ICheckoutV2SetupIntentData & { success: true },
    confirmedIntent?: SetupIntent | PaymentIntent,
  ): Promise<void> => {
    const result = await checkout.pollBasket(basketId);

    if (result === null) {
      datadog.logger.error(`Timed out while waiting for new basket '${basketId}' to complete`, {
        eventName: "PollingNewBasketTimedOut",
        intentData,
        confirmedIntent,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(!!paymentData);
      return;
    }

    if (result.status === CheckoutStatus.SUCCESS) {
      datadog.logger.info(`New basket '${basketId}' complete with status '${result.status}'`, {
        eventName: "PollingNewBasketSuccessful",
        intentData,
        confirmedIntent,
        result,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      setPaymentState(DisplayState.COMPLETE);
      onSuccess();
    } else {
      datadog.logger.error(`New basket '${basketId}' complete with status '${result.status}'`, {
        eventName: "PollingNewBasketUnsuccessful",
        intentData,
        confirmedIntent,
        result,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError();
    }
  };

  const handleFailedBasket = async (
    basketId: string,
    intentConfirmation: IIntentConfirmationResultFailure | IIntentConfirmationResultError,
  ) => {
    if (intentConfirmation.intentFailed) {
      try {
        await checkout.postPaymentsBasketConfirmed({ basketId });
      } catch (e) {
        const intentType = getIntentClientSecretType(intentConfirmation.intent?.id || "");
        const intentTypeFormatted = formatIntentType(intentType);

        datadog.logger.error(`Failed to notify BE for failed ${intentTypeFormatted} for basket '${basketId}': ${e}`, {
          eventName: `${intentTypeFormatted}IntentFailureNotificationFailed`,
          intentConfirmation,
          "data-component": CardDetailsFormCheckoutV2.name,
        }, e as Error);
      }
    }
  };

  const handleNewBasket = async (
    basketId: string,
    paymentData?: PaymentRequestPaymentMethodEvent,
    intentData?: ICheckoutV2SetupIntentData & { success: true },
    confirmedIntent?: SetupIntent | PaymentIntent,
  ): Promise<void> => {

    setQuickCheckoutError(undefined);
    setError(undefined);
    setPaymentState(DisplayState.PROCESSING);

    try {
      const result = await checkout.postPaymentsBasketConfirmed({ basketId });

      if (result.status === CheckoutStatus.SUCCESS) {
        datadog.logger.info(`New basket '${basketId}' complete with status '${result.status}'`, {
          eventName: "NewBasketSuccessful",
          intentData,
          confirmedIntent,
          result,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        setPaymentState(DisplayState.COMPLETE);
        onSuccess();
      } else {
        datadog.logger.error(`New basket '${basketId}' confirmation failed with status '${result.status}'`, {
          eventName: "NewBasketUnsuccessful",
          intentData,
          confirmedIntent,
          result,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        handleError();
      }
    } catch (basketConfirmationError) {
      if (isTransientError(basketConfirmationError)) {
        await handlePollingNewBasket(basketId, paymentData, intentData, confirmedIntent);
      } else {
        const errorStatusMessage = basketConfirmationError instanceof AxiosError ? `with status '${basketConfirmationError.status}'` : "";
        datadog.logger.error(`New basket '${basketId}' confirmation failed ${errorStatusMessage}`, {
          eventName: "NewBasketUnsuccessful",
          intentData,
          confirmedIntent,
          basketConfirmationError,
          "data-component": CardDetailsFormCheckoutV2.name,
        });
        handleError();
      }
    }
  };

  /** @param paymentData If provided, the data used for quick-checkout. */
  const handleSubmitInternal: (paymentData?: PaymentRequestPaymentMethodEvent) => Promise<void> =
    async (paymentData) => {
      setPaymentState(DisplayState.PROCESSING);

      const isDeliveryDateValid = await validateDeliveryDate();
      if (!isDeliveryDateValid) {
        return;
      }

      const stripeIntent = await getStripeIntent();

      if (!stripeIntent.success) {
        handleError(paymentData, stripeIntent.message, undefined, stripeIntent.failedOn);
        return;
      }

      const {
        intentClientSecret,
        basketId,
        existingBasket,
      } = stripeIntent;

      try {
        if (existingBasket) {
          datadog.logger.info(`[CardDetailsFormCheckoutV2] Using existing basket - existingBasket: ${existingBasket}, flowSlug: ${flowSlug}`, {
            eventName: "UsingExistingBasket",
            existingBasket,
            flowSlug,
            "data-component": CardDetailsFormCheckoutV2.name,
          });
          await handleExistingBasket(existingBasket, paymentData, stripeIntent);
          return;
        }

        datadog.logger.debug("No existing basket, will attempt to create new one", {
          eventName: "ActionReturnedNoBasket",
          stripeIntent,
          paymentData,
          "data-component": CardDetailsFormCheckoutV2.name,
        });

        if (!intentClientSecret) {
          datadog.logger.error("No client secret for stripeIntent", {
            eventName: "ActionReturnedNoSecret",
            stripeIntent,
            "data-component": CardDetailsFormCheckoutV2.name,
          });
          handleError(paymentData);
          return;
        }

        const intentType = getIntentClientSecretType(intentClientSecret);
        const formattedIntentType = formatIntentType(intentType);

        const intentConfirmation = await confirmPaymentData(intentClientSecret, paymentData);
        if (!intentConfirmation.success) {
          await handleFailedBasket(
            basketId,
            intentConfirmation,
          );
          return;
        }

        datadog.logger.info(`${formattedIntentType} intent succeeded, now polling basket '${basketId}'...`, {
          eventName: `${formattedIntentType}IntentSucceeded`,
          stripeIntent,
          intentConfirmation,
          "data-component": CardDetailsFormCheckoutV2.name,
        });

        await handleNewBasket(basketId, paymentData, stripeIntent, intentConfirmation.intent);
      } catch (err) {

        datadog.logger.error("handleSubmit failed", {
          eventName: "HandeSubmitUnhandledException",
          stripeIntent,
          "data-component": CardDetailsFormCheckoutV2.name,
        }, err as Error);

        handleError(paymentData, (err as Error)?.message ?? undefined);
      }
    };

  type AsyncFormEventHandler = FormEventHandler<HTMLFormElement> &
  ((event: React.FormEvent<HTMLFormElement>) => Promise<void>);

  const handleSubmit: AsyncFormEventHandler = async (event) => {
    event.preventDefault();
    datadog.logger.log("Normal-checkout starting", {
      eventName: "NormalCheckoutStarted",
      event,
      "data-component": CardDetailsFormCheckoutV2.name,
    });

    try {
      await handleSubmitInternal(undefined);
      posthog.capture("user_action_custom", {
        type: "manual_checkout",
      });
    } catch (cause) {
      datadog.logger.error("Unhandled exception during normal-checkout handleSubmitInternal", {
        eventName: "NormalCheckoutUnhandledExceptionSubmit",
        event,
        "data-component": CardDetailsFormCheckoutV2.name,
      }, cause as Error);
      handleError();
    }
  };

  const processingPhrases = useMemo<string[]>(() => processingModalPhraseMap[PRODUCT_CATEGORIES[flow.mappedFlow.product ?? "FRESH"]] || [], [ flow.mappedFlow.product ]);

  useEffect(() => {
    setIsCardDetailsComplete(isCardNumberComplete && isCVCComplete && isExpiryComplete);
  }, [ isCardNumberComplete, isCVCComplete, isExpiryComplete, discountStore ]);

  useEffect(() => {
    if (stripe) {
      const pr: PaymentRequest = stripe.paymentRequest({
        country: "GB",
        currency: "gbp",
        total: {
          label: "KatKin Trial",
          amount: price || 0,
        },
        displayItems: [],
        requestPayerName: true,
        requestPayerEmail: false,
        requestPayerPhone: true,
        requestShipping: true,
        shippingOptions: [ { id: "free", label: "Free Shipping", detail: "Free shipping by DPD", amount: 0 } ],
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) {
          datadog.logger.debug("Quick-checkout available", {
            eventName: "QuickCheckoutSupportSupported",
            result,
            price,
            pr,
            "data-component": CardDetailsFormCheckoutV2.name,
          });
          setPaymentRequestResult(result);
          setPaymentRequest(pr);
          setCanUseQuickCheckout(true);
        } else {
          datadog.logger.warn("No data returned for quick-checkout support check", {
            eventName: "QuickCheckoutSupportNoData",
            result,
            "data-component": CardDetailsFormCheckoutV2.name,
          });
        }
      }).catch((paymentError) => datadog.logger.error("Could not check for quick-checkout support", {
        eventName: "QuickCheckoutSupportFailed",
        paymentError,
        "data-component": CardDetailsFormCheckoutV2.name,
      }, paymentError as Error));

    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [ stripe ]);

  useEffect(() => {
    if (stripe && paymentRequest && price) {
      datadog.logger.debug(`Updating paymentRequest price to ${price}`, {
        eventName: "QuickCheckoutPriceUpdated",
        price,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      paymentRequest.update({
        total: {
          label: "KatKin Trial",
          amount: price,
        },
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [ stripe, paymentRequest, price ]);

  const isInvalidShippingAddress = (event: PaymentRequestPaymentMethodEvent): boolean => {
    const address = event.shippingAddress;
    return (
      address?.country?.toUpperCase() !== "GB" ||
    !address?.postalCode ||
    !address?.addressLine?.length ||
    !address?.city ||
    !event.payerPhone
    );
  };

  /** @return true if there was an error with the address */
  const fillAddress = (event: PaymentRequestPaymentMethodEvent): boolean => {
    setPaymentState(DisplayState.PROCESSING);
    if (isInvalidShippingAddress(event)) {
      datadog.logger.warn("PaymentRequestInvalidAddress", {
        eventName: "PaymentRequestInvalidAddress",
        eventData: event,
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      handleError(
        event,
        "There was a problem with your address, please fill in your address below.",
        "invalid_shipping_address",
      );
      return true;
    }

    datadog.logger.info("PaymentRequestValidAddress", {
      eventName: "PaymentRequestValidAddress",
      eventData: event,
      "data-component": CardDetailsFormCheckoutV2.name,
    });

    const addressList = event.shippingAddress?.addressLine;
    const half = Math.ceil(addressList?.length ?? 0 / 2);
    const addressLine1 = addressList?.slice(0, half).reduce((acc, curr) => `${acc}${acc ? ", " : ""}${curr}`, "") ?? "";
    const addressLine2 = addressList?.slice(-half).reduce((acc, curr) => `${acc}${acc ? ", " : ""}${curr}`, "") ?? "";

    const address = {
      line1: addressLine1 ?? "",
      line2: addressLine2 ?? "",
      city: event.shippingAddress?.city ?? "",
      country: event.shippingAddress?.country,
      postcode: event.shippingAddress?.postalCode ?? "",
      phone: event.payerPhone ?? "",
    };
    setFlowFieldValue(flowSlug, "address", { displayValue: {
      billingAddress: address,
      shippingAddress: address,
    },
    submitValue: {
      billingAddress: address,
      shippingAddress: address,
    } });
    posthog.capture("user_action_custom", {
      type: "quick_checkout",
      data: {
        address: {
          billingAddress: address,
          shippingAddress: address,
        },
      },
    });
    datadog.logger.info("quick checkout address filled-in, proceeding with checkout", {
      eventName: "PaymentRequestAddressFilledIn",
      eventData: event,
      address,
      "data-component": CardDetailsFormCheckoutV2.name,
    });
    return false;
  };

  const handlePaymentMethod = async (event: PaymentRequestPaymentMethodEvent): Promise<void> => {
    try {
      if (fillAddress(event)) {
        return;
      }
    } catch (cause) {
      datadog.logger.error("Unhandled exception during fillAddress", {
        eventName: "QuickCheckoutUnhandledExceptionFillAddress",
        event,
        "data-component": CardDetailsFormCheckoutV2.name,
      }, cause as Error);
      handleError();
      return;
    }

    try {
      await handleSubmitInternal(event);
    } catch (cause) {
      datadog.logger.error("Unhandled exception during quick-checkout handleSubmitInternal", {
        eventName: "QuickCheckoutUnhandledExceptionSubmit",
        event,
        "data-component": CardDetailsFormCheckoutV2.name,
      }, cause as Error);
      handleError();
    }
  };

  useEffect(() => {
    if (!stripe || !paymentRequest || !paymentRequestResult) {
      return;
    }

    paymentRequest.on("paymentmethod", async (event) => {
      datadog.logger.log("Quick-checkout starting", {
        eventName: "QuickCheckoutStarted",
        event,
        "data-component": CardDetailsFormCheckoutV2.name,
      });

      await handlePaymentMethod(event);
    });

    paymentRequest.on("cancel", () => {
      datadog.logger.warn("Quick-checkout element dismissed (on cancel called)", {
        eventName: "QuickCheckoutCancelled",
        "data-component": CardDetailsFormCheckoutV2.name,
      });
      setPurchaseInProgress(false);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [ paymentRequest, paymentRequestResult, stripe ]);

  useEffect(() => {
    if (posthog) {
      const flagValue = posthog.featureFlags.getFeatureFlag("paypal-painted-door-test-3");
      setShowPayPalButton(flagValue === "test");
    }
  }, [ posthog ]);

  return (
    <>
      { (paymentType === "quickCheckout") ? (
        <>
          { paymentRequest && (
            <Column>
              <PaymentRequestButtonElement
                options={{ paymentRequest }}
                onClick={() => setPurchaseInProgress(true)}
              />
            </Column>
          ) }

          { showPayPalButton && (
            <Column>
              <PayPalButton/>
            </Column>
          ) }

          { quickCheckoutError && (
            <Column>
              <Spacer size="md" />
              <FormError>{ quickCheckoutError }</FormError>
            </Column>
          ) }
        </>
      ) : (
        <Form action="" onSubmit={handleSubmit}>
          <fieldset>
            <Grid>
              <Column>
                <Label label="Card number" hideLabel={false} UUID="cardNumber" fontStyle="alternative">
                  <CardNumberElement
                    className={cardInputClasses}
                    options={cardInputOptions}
                    onChange={(event) => setIsCardNumberComplete(event.complete)}
                  />
                </Label>
              </Column>
              <Column spans={6}>
                <Label label="CVC" hideLabel={false} UUID="cardCvc" fontStyle="alternative">
                  <CardCvcElement
                    className={cardInputClasses}
                    options={cardInputOptions}
                    onChange={(event) => setIsCVCComplete(event.complete)}
                  />
                </Label>
              </Column>
              <Column spans={6}>
                <Label label="Expiry" hideLabel={false} UUID="cardExpiry" fontStyle="alternative">
                  <CardExpiryElement
                    className={cardInputClasses}
                    options={cardInputOptions}
                    onChange={(event) => setIsExpiryComplete(event.complete)}
                  />
                </Label>
              </Column>
              <>
                { error && (
                  <Column >
                    <FormError>{ error }</FormError>
                  </Column>
                ) }
              </>
            </Grid>
          </fieldset>
          <Spacer size="xl" />
          <Grid>
            <Column justify="center" align="center">
              <Button type="submit" disabled={!stripe || isDisabled || !isCardDetailsComplete} state={paymentState}>
                <span className="flex justify-center items-center"><Icon icon="padlock" color="inherit" size="xsmall" /><span className="ml-2">Place my order</span></span>
              </Button>
            </Column>
          </Grid>
          <Section
            theme="brand"
            size="sm"
            className={clsx(
              "fixed bottom-0 left-0 w-full p-4 z-30",
            )}>
            <Container
              className="flex items-center justify-center flex-col-reverse md:flex-row"
            >
              <Button type="submit" disabled={!stripe || isDisabled || !isCardDetailsComplete} state={paymentState} color="secondary">
                <span className="flex justify-center items-center"><Icon icon="padlock" color="inherit" size="xsmall" /><span className="ml-2">Place my order</span></span>
              </Button>
            </Container>
          </Section>
        </Form>
      ) }

      { (paymentState === DisplayState.COMPLETE || paymentState === DisplayState.PROCESSING) && (
        <ProcessingPaymentModal phrases={processingPhrases}
          processingComplete={paymentState === DisplayState.COMPLETE}
        />
      ) }

    </>
  );
};

export default CardDetailsFormCheckoutV2;
